import React, { useEffect, useRef } from "react";

/**
 * Projector-beam dust particles.
 * Canvas-based for performance. Each particle drifts gently, glows
 * brighter when near the dynamic light center (cursor-aware), and
 * subtly responds to scroll velocity.
 *
 * Props:
 *   - count: number of particles (default ~110)
 *   - color: rgb string for particle core (default warm white)
 *   - className: extra positioning classes for the canvas
 */
export const DustParticles = ({
    count = 110,
    color = "255,235,200",
    className = "",
}) => {
    const canvasRef = useRef(null);
    const stateRef = useRef({
        particles: [],
        mouse: { x: 0.5, y: 0.4, vx: 0, vy: 0 },
        scrollVel: 0,
        lastScroll: 0,
        running: true,
    });

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d", { alpha: true });
        const DPR = Math.min(window.devicePixelRatio || 1, 2);

        const resize = () => {
            const rect = canvas.getBoundingClientRect();
            canvas.width = Math.floor(rect.width * DPR);
            canvas.height = Math.floor(rect.height * DPR);
            ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
        };
        resize();
        window.addEventListener("resize", resize);

        // Seed particles
        const rect = canvas.getBoundingClientRect();
        const W = rect.width;
        const H = rect.height;
        const state = stateRef.current;
        state.particles = Array.from({ length: count }).map(() => {
            const z = Math.random(); // depth 0..1 (near .. far)
            return {
                x: Math.random() * W,
                y: Math.random() * H,
                z,
                r: 0.4 + (1 - z) * 2.2,
                baseAlpha: 0.18 + (1 - z) * 0.45,
                vx: (Math.random() - 0.5) * 0.12 * (1 - z + 0.4),
                vy: -0.05 - Math.random() * 0.18 * (1 - z + 0.3), // drift upward
                tw: Math.random() * Math.PI * 2, // twinkle phase
                twS: 0.004 + Math.random() * 0.008,
            };
        });

        const onMove = (e) => {
            const r = canvas.getBoundingClientRect();
            const nx = (e.clientX - r.left) / r.width;
            const ny = (e.clientY - r.top) / r.height;
            state.mouse.vx = nx - state.mouse.x;
            state.mouse.vy = ny - state.mouse.y;
            state.mouse.x = nx;
            state.mouse.y = ny;
        };
        const onScroll = () => {
            const s = window.scrollY;
            state.scrollVel = s - state.lastScroll;
            state.lastScroll = s;
        };
        window.addEventListener("mousemove", onMove, { passive: true });
        window.addEventListener("scroll", onScroll, { passive: true });

        let rafId;
        const tick = () => {
            if (!state.running) return;
            const r = canvas.getBoundingClientRect();
            const W2 = r.width;
            const H2 = r.height;
            ctx.clearRect(0, 0, W2, H2);

            // Beam center: blended cursor + a default high-center anchor.
            const beamX = state.mouse.x * 0.6 + 0.5 * 0.4; // pull toward center
            const beamY = state.mouse.y * 0.4 + 0.18 * 0.6; // bias upward (projector source)
            const beamPxX = beamX * W2;
            const beamPxY = beamY * H2;

            // Scroll-driven horizontal nudge factor
            const scrollPush = Math.max(-3, Math.min(3, state.scrollVel * 0.05));
            // Decay scroll velocity each frame
            state.scrollVel *= 0.86;
            // Mouse velocity decay
            state.mouse.vx *= 0.92;
            state.mouse.vy *= 0.92;

            for (const p of state.particles) {
                // Gentle drift
                p.x += p.vx + state.mouse.vx * 8 * (1 - p.z) + scrollPush * (1 - p.z * 0.6);
                p.y += p.vy + state.mouse.vy * 6 * (1 - p.z);

                // Wrap edges
                if (p.x < -10) p.x = W2 + 10;
                if (p.x > W2 + 10) p.x = -10;
                if (p.y < -10) p.y = H2 + 10;
                if (p.y > H2 + 10) p.y = -10;

                // Twinkle
                p.tw += p.twS;
                const tw = 0.65 + Math.sin(p.tw) * 0.35;

                // Distance to beam center (normalized)
                const dx = (p.x - beamPxX) / W2;
                const dy = (p.y - beamPxY) / H2;
                const dist = Math.sqrt(dx * dx + dy * dy);
                const beam = Math.max(0, 1 - dist * 1.8); // 0..1 inside beam radius

                const alpha = Math.min(
                    0.95,
                    p.baseAlpha * tw + beam * 0.55
                );

                // Soft glow
                const radius = p.r * (1 + beam * 1.4);
                ctx.beginPath();
                ctx.fillStyle = `rgba(${color}, ${alpha})`;
                ctx.shadowColor = `rgba(${color}, ${alpha * 0.7})`;
                ctx.shadowBlur = 6 + beam * 12;
                ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
                ctx.fill();
            }
            ctx.shadowBlur = 0;

            rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);

        // Respect reduced motion: still render but slow drift
        const prefersReduced = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        ).matches;
        if (prefersReduced) {
            state.particles.forEach((p) => {
                p.vx *= 0.2;
                p.vy *= 0.2;
                p.twS *= 0.5;
            });
        }

        return () => {
            state.running = false;
            cancelAnimationFrame(rafId);
            window.removeEventListener("resize", resize);
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("scroll", onScroll);
        };
    }, [count, color]);

    return (
        <canvas
            ref={canvasRef}
            aria-hidden="true"
            data-testid="dust-particles"
            className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
            style={{ mixBlendMode: "screen" }}
        />
    );
};
